import { ExternalLink, TrendingUp, MessageCircle, Clock, Smartphone } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export default function Benchmarks() {
  const { language } = useLanguage();

  return (
    <div className="relative">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-slate-950" />
        <div className="absolute top-1/4 right-1/4 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[120px] pointer-events-none" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-100 leading-tight tracking-tight">
              {language === 'pt' ? 'O custo invisível da sua operação atual' : 'The hidden cost of your current operation'}
            </h1>
            <p className="mt-6 text-lg lg:text-xl text-slate-400 leading-relaxed max-w-3xl">
              {language === 'pt' 
               ? 'Não operamos com achismos. A tecnologia da ProcessSurge é baseada no comportamento real do consumidor e desenhada para estancar a perda de dinheiro no seu e-commerce.'
               : 'We do not operate on hunches. ProcessSurge technology is based on real consumer behavior and designed to stop the loss of money in your e-commerce.'}
            </p>
          </div>
        </div>
      </section>

      {/* Benchmarks Grid */}
      <section className="py-16 lg:py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8">
            
            {/* 1. Cart Abandonment (Baymard) */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-3xl p-8 lg:p-10 hover:border-slate-700 transition-colors">
              <div className="grid lg:grid-cols-3 gap-8 items-center">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-red-400" />
                    </div>
                    <h3 className="font-outfit text-xl font-bold text-slate-100">
                      {language === 'pt' ? 'Abandono de Carrinho' : 'Cart Abandonment'}
                    </h3>
                  </div>
                </div>
                <div className="text-center lg:text-left">
                  <p className="text-5xl lg:text-6xl font-outfit font-black text-red-400">
                    69.99%
                  </p>
                </div>
                <div>
                  <p className="text-slate-400 mb-4 leading-relaxed">
                    {language === 'pt' 
                     ? 'É a taxa média global de abandono. Você paga pelo tráfego, o cliente entra na loja e 7 em cada 10 vão embora na hora de pagar. Sem automação ativa, esse dinheiro está perdido.' 
                     : 'This is the global average abandonment rate. You pay for traffic, the customer enters the store, and 7 out of 10 leave at checkout. Without active automation, that money is lost.'}
                  </p>
                  <a
                    href="https://baymard.com/lists/cart-abandonment-rate"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 text-sm font-medium transition-colors"
                  >
                    Baymard Institute
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>

            {/* 2. WhatsApp vs Email Open Rate */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-3xl p-8 lg:p-10 hover:border-slate-700 transition-colors">
              <div className="grid lg:grid-cols-3 gap-8 items-center">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center">
                      <Smartphone className="w-6 h-6 text-emerald-400" />
                    </div>
                    <h3 className="font-outfit text-xl font-bold text-slate-100">
                      {language === 'pt' ? 'Taxa de Abertura' : 'Open Rate'}
                    </h3>
                  </div>
                </div>
                <div className="text-center lg:text-left">
                  <div className="flex flex-col gap-4 max-w-xs mx-auto lg:mx-0">
                    <div className="flex items-center gap-4">
                        <span className="text-slate-500 w-20 text-right font-medium">E-mail</span>
                        <div className="h-3 flex-1 bg-slate-800 rounded-full overflow-hidden">
                            <div className="h-full bg-slate-500 w-[20%] rounded-full"></div>
                        </div>
                        <span className="text-xl font-bold text-slate-500 w-16 text-left">~20%</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="text-emerald-400 w-20 text-right font-medium">WhatsApp</span>
                        <div className="h-3 flex-1 bg-emerald-900/30 rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-400 w-[98%] rounded-full shadow-[0_0_10px_rgba(52,211,153,0.5)]"></div>
                        </div>
                        <span className="text-2xl font-bold text-emerald-400 w-16 text-left">~98%</span>
                    </div>
                  </div>
                </div>
                <div>
                  <p className="text-slate-400 mb-3 leading-relaxed">
                    {language === 'pt' 
                     ? 'O e-mail marketing virou panfleto digital. O WhatsApp é lido quase instantaneamente. A nossa tecnologia coloca a sua oferta de recuperação direto no bolso do cliente.'
                     : 'Email marketing has become digital flyers. WhatsApp is read almost instantly. Our technology puts your recovery offer right in the customer\'s pocket.'}
                  </p>
                  <p className="text-xs text-slate-500 font-medium">
                      Sources: Mailchimp Benchmarks / Sinch Market Report
                  </p>
                </div>
              </div>
            </div>

            {/* 3. Speed to Lead */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-3xl p-8 lg:p-10 hover:border-slate-700 transition-colors">
              <div className="grid lg:grid-cols-3 gap-8 items-center">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center">
                      <Clock className="w-6 h-6 text-blue-400" />
                    </div>
                    <h3 className="font-outfit text-xl font-bold text-slate-100">
                      {language === 'pt' ? 'O Fator Tempo' : 'The Speed Factor'}
                    </h3>
                  </div>
                </div>
                <div className="text-center lg:text-left">
                     <p className="text-5xl lg:text-6xl font-outfit font-black text-blue-400">
                        391%
                    </p>
                </div>
                <div>
                  <p className="text-slate-400 mb-3 leading-relaxed">
                    {language === 'pt'
                     ? 'É o aumento na taxa de conversão ao responder um lead no primeiro minuto. Com humanos a média é de 47 horas. Nossa Inteligência Artificial responde em 3 segundos.'
                     : 'This is the conversion rate increase when responding to a lead in the first minute. Humans average 47 hours. Our Artificial Intelligence responds in 3 seconds.'}
                  </p>
                  <span className="text-xs text-slate-500 font-medium">Source: Velocify / Harvard Business Review</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
